import { useContext, useEffect, useMemo, useState } from 'react'
import { useRouteMatch, useLocation, useHistory } from 'react-router-dom'
import CustomGroupRow from 'views/panels/hospital/Lab/Table/CustomGroupRow'
import DataTable from 'components/Table/DataTable'
import { createTableStyle } from '../Action'
import objectPath from 'object-path'

import renderButton from '../render/Button'
import {
  renderLabel,
  renderActive,
  renderCheckbox,
  renderCurrency,
  renderDate,
  renderDateTime,
  renderImage,
  renderMediaId,
  renderOrderNumber,
  renderSelect,
  renderStatus,
  renderString,
  renderArrayToString,
  renderTime,
  renderHtml,
  getDataValue,
  renderMultiField,
} from '../Render'

import { DesignContext } from 'contexts/DesignContext'
import { HistoryContext } from 'contexts/HistoryContext'
import { useTimeFormat } from 'contexts/TimeFormatContext'
import Table from '../Table'
import { checkWithDefaultValue } from '../action/index'
import { onRowTableUpdate } from '../action/EditRow'

const SubTablePanel = ({
  columnKey,
  rowData,
  tableData = [],
  detailColumns,
  uischema,
  formData,
  setModalAction,
  renderTableData,
  mainColumn,
  isArrayTable,
  propAction,
}) => {
  const history = useHistory()
  const match = useRouteMatch()
  const location = useLocation()
  let search = location.search || []
  let receivePathParams = match.params
  let urlSearchParams = new URLSearchParams(search)
  let receiveQueryParams = Object.fromEntries([...urlSearchParams])
  let allParams = { ...receivePathParams, ...receiveQueryParams }
  const designContext = useContext(DesignContext)
  const historyContext = useContext(HistoryContext)
  const { formatDate, formatTime, formatDateTime } = useTimeFormat()
  const [editable, setEditable] = useState({})

  const getRenderColumn = (column) => {
    switch (column.typeField ?? column.type) {
      case 'label':
        return renderLabel({ column })
      case 'string':
        return renderString({ column })
      case 'arrayToString':
        return renderArrayToString({ column })
      case 'multiField':
        return renderMultiField({ column })
      case 'no':
        return renderOrderNumber({ column })
      case 'currency':
        return renderCurrency({ column })
      case 'date':
        return renderDate({ column, formatDate })
      case 'time':
        return renderTime({ column, formatTime })
      case 'datetime':
        return renderDateTime({ column, formatDateTime })
      case 'checkbox':
        return renderCheckbox({ column })
      case 'active':
        return renderActive({ column })
      case 'status':
        return renderStatus({ column })
      case 'select':
        return renderSelect({ column })
      case 'image':
        return renderImage({ column })
      case 'mediaId':
        return renderMediaId({ column })
      case 'button':
        return renderButton(
          column,
          formData,
          allParams,
          location,
          history,
          designContext,
          historyContext,
          uischema,
          setModalAction,
          renderTableData
        )
      case 'html':
        return renderHtml(column)
      default:
        return
    }
  }

  const tableColumn = useMemo(() => {
    let newColumn = []
    detailColumns.forEach((column) => {
      if (column?.typeField === 'system') return
      column = createTableStyle(column, uischema)
      column.render = getRenderColumn(column)
      newColumn.push(column)
    })

    return newColumn
  }, [columnKey])

  const onRowUpdate = (newData, oldData) =>
    new Promise((resolve, reject) => {
      setTimeout(() => {
        let oldRowData = rowData
        let newRowData = rowData
        let refId = mainColumn.refIdDetailPanel
        let subData = objectPath.get(rowData, columnKey)
        let index = subData.findIndex((item) => {
          return objectPath.get(item, refId) === objectPath.get(oldData, refId)
        })
        if (index > -1) {
          newData = checkWithDefaultValue(newData, {
            tableColumn: detailColumns,
          })
          subData[index] = newData
          if (isArrayTable) {
            objectPath.set(newRowData, columnKey, subData)
            onRowTableUpdate(newRowData, oldRowData, propAction)
          }
        }
        resolve()
      }, 1000)
    })

  const onRowDelete = (oldData) =>
    new Promise((resolve, reject) => {
      setTimeout(() => {
        let oldRowData = rowData
        let newRowData = rowData
        let refId = mainColumn.refIdDetailPanel
        let subData = objectPath.get(rowData, columnKey)
        let index = subData.findIndex((item) => {
          return objectPath.get(item, refId) === objectPath.get(oldData, refId)
        })
        if (index > -1) {
          oldData = checkWithDefaultValue(oldData, {
            tableColumn: detailColumns,
          })

          let hasDeleteMode = detailColumns.find(
            ({ field }) => field === 'deleteMode'
          )
          if (hasDeleteMode) {
            objectPath.set(subData, [index, `@isDeleted`], true)
            objectPath.set(subData, [index, 'deleteMode'], 1)
          } else {
            objectPath.del(subData, index)
          }
          if (isArrayTable) {
            objectPath.set(newRowData, columnKey, subData)
            onRowTableUpdate(newRowData, oldRowData, propAction)
          }
        }
        resolve()
      }, 1000)
    })

  useEffect(() => {
    if (mainColumn) {
      let editable = {}
      if (mainColumn?.isEditDetailPanel) {
        editable.onRowUpdate = (newData, oldData) =>
          onRowUpdate(newData, oldData)
      }
      if (mainColumn?.isDeleteDetailPanel) {
        editable.onRowDelete = (oldData) => onRowDelete(oldData)
      }
      setEditable(editable)
    }
  }, [columnKey])

  const customRow = (rowData) => {
    return (
      <tr>
        <td colSpan="5">
          <table className="w-100">
            <CustomGroupRow {...rowData} />
          </table>
        </td>
      </tr>
    )
  }

  return (
    <Table
      components={{
        Toolbar: (props) => null,
        Groupbar: (props) => null,
        GroupRow: (rowData) => customRow(rowData),
      }}
      editable={editable}
      columns={tableColumn}
      data={tableData.filter((item) => !objectPath.get(item, '@isDeleted'))}
      options={{
        grouping: true,
        search: false,
        showTitle: false,
        rowStyle: (rowData) => {
          let found = null
          tableColumn.forEach((column) => {
            let conditions = column?.conditions
            if (conditions) {
              found = conditions.find(({ value, rowColor }) => {
                return (
                  value === objectPath.get(rowData, column.field) && rowColor
                )
              })
            }
          })
          if (found) {
            return {
              backgroundColor: found.rowColor,
              color: found.rowTextColor,
            }
          }
          return {}
        },
      }}
    />
  )
}

const TablePanel = (props) => {
  let { rowData, detailColumns, tableColumn } = props
  let keys = Object.keys(detailColumns)

  return (
    <>
      {keys.map((key) => {
        let itemColumn = []
        let table = []
        let columns = objectPath.get(detailColumns, key)
        let isHtml = objectPath.get(columns, 'type') === 'html'
        let value = objectPath.get(rowData, key)
        let mainColumn = tableColumn.find(({ field }) => field === key)

        if (isHtml) {
          let itemTable = {}
          objectPath.set(itemTable, columns.field, value)
          table.push(itemTable)
          tableColumn.push(columns)
        } else {
          if (typeof value === 'string') {
            table = JSON.parse(value)
          } else {
            table = value
          }
          if (Array.isArray(table)) {
            table.map((table, index) => {
              objectPath.set(table, 'tableData.no', index + 1)
              if (columns) {
                columns.forEach((column) => {
                  let { field } = column
                  if (objectPath.has(table, field)) return
                  let value = getDataValue(column, table)
                  objectPath.set(table, field, value)
                })
              }
              return table
            })
          }
          itemColumn = columns
        }
        if (table?.length === 0 || !table) {
          return
        }

        return (
          <SubTablePanel
            {...props}
            columnKey={key}
            tableData={table}
            mainColumn={mainColumn}
            detailColumns={itemColumn}
          />
        )
      })}
    </>
  )
}

export default TablePanel
